import React from 'react';
import moment from 'moment';

/**
 * Customer fiscal receipt (Kundenbeleg) for the Getränkemarkt — a KassenSichV
 * Beleg sized for the 80mm Bixolon thermal printer. Hidden on screen; the print
 * stylesheet hides everything else and shows just this block. Printing is
 * triggered by the parent via window.print().
 *
 * `receipt` is the context object from GET /markt/transaction/<id>/receipt/
 * (seller, lines, totals, TSE data, and pre-rendered QR PNG data-URIs). When the
 * transaction isn't TSE-signed yet (`tse_failed`), we print the legally required
 * "Sicherheitseinrichtung ausgefallen" note instead of the TSE block.
 */
const euro = (value) => {
  const n = Number(value);
  if (Number.isNaN(n)) return String(value);
  return `${n.toLocaleString('de-DE', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })} €`;
};

const qtyOf = (line) => {
  const q = parseFloat(line?.item?.quantity);
  return Number.isNaN(q) ? line?.item?.quantity : q;
};

const lineSum = (line) => line?.vat_amounts?.[0]?.incl_vat;

const FiscalReceipt = ({ receipt }) => {
  return (
    <div className='fiscal-receipt' aria-hidden='true'>
      <style>{`
        .fiscal-receipt { display: none; }
        @media print {
          @page { size: 80mm auto; margin: 3mm; }
          body * { visibility: hidden !important; }
          .fiscal-receipt, .fiscal-receipt * { visibility: visible !important; }
          .fiscal-receipt {
            display: block !important;
            position: absolute; left: 0; top: 0;
            width: 72mm !important; min-width: 72mm; box-sizing: border-box;
            font-family: Arial, Helvetica, sans-serif;
            color: #000 !important;
            font-weight: 700;
            line-height: 1.3;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          .fiscal-receipt > div { width: 100%; }
          .fiscal-receipt h2 { font-size: 22px; font-weight: 900; margin: 0 0 4px; text-align: center; }
          .fiscal-receipt .seller { font-size: 15px; text-align: center; }
          .fiscal-receipt .meta { font-size: 15px; }
          .fiscal-receipt table.items { width: 100%; border-collapse: collapse; margin-top: 4px; }
          .fiscal-receipt table.items td { font-size: 16px; vertical-align: top; padding: 3px 0; }
          .fiscal-receipt td.qty { white-space: nowrap; padding-right: 6px; font-weight: 900; }
          .fiscal-receipt td.sum { text-align: right; white-space: nowrap; font-weight: 900; }
          .fiscal-receipt table.totals { width: 100%; border-collapse: collapse; }
          .fiscal-receipt table.totals td { font-size: 16px; padding: 2px 0; }
          .fiscal-receipt table.totals td.r { text-align: right; white-space: nowrap; }
          .fiscal-receipt tr.grand td { font-size: 20px; font-weight: 900; padding-top: 4px; }
          .fiscal-receipt hr { border: none; border-top: 2px solid #000; margin: 6px 0; }
          .fiscal-receipt .tse { font-size: 13px; word-break: break-all; overflow-wrap: anywhere; }
          .fiscal-receipt .tse div { margin: 2px 0; }
          .fiscal-receipt .qrblock { text-align: center; margin-top: 10px; }
          .fiscal-receipt .qrblock img { width: 42mm; height: 42mm; }
          .fiscal-receipt .qrlabel { font-size: 13px; font-weight: 700; margin-top: 3px; }
          .fiscal-receipt .warn { font-size: 17px; font-weight: 900; border: 2px solid #000; padding: 5px; text-align: center; margin: 6px 0; }
          .fiscal-receipt .foot { font-size: 15px; text-align: center; margin-top: 10px; }
        }
      `}</style>
      {receipt && (
        <div>
          <h2>{receipt.seller?.name}</h2>
          <div className='seller'>
            {receipt.seller?.street}
            <br />
            {receipt.seller?.postal_code} {receipt.seller?.city}
            <br />
            Steuernr.: {receipt.seller?.tax_number}
          </div>
          <hr />
          <div className='meta'>
            {moment(receipt.time_created).format('DD.MM.YYYY HH:mm')}
          </div>
          <div className='meta'>
            Beleg: {String(receipt.id || '—').slice(0, 8)}
          </div>

          <table className='items'>
            <tbody>
              {(receipt.lines || []).map((line, idx) => (
                <tr key={idx}>
                  <td className='qty'>{qtyOf(line)}×</td>
                  <td>{line.text}</td>
                  <td className='sum'>{euro(lineSum(line))}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <hr />
          <table className='totals'>
            <tbody>
              <tr className='grand'>
                <td>SUMME</td>
                <td className='r'>{euro(receipt.totals?.gross)}</td>
              </tr>
              <tr>
                <td>Zahlart: Bar</td>
                <td className='r'>{euro(receipt.payment?.amount)}</td>
              </tr>
              <tr>
                <td>Netto</td>
                <td className='r'>{euro(receipt.totals?.net)}</td>
              </tr>
              <tr>
                <td>zzgl. MwSt {receipt.totals?.vat_percentage}%</td>
                <td className='r'>{euro(receipt.totals?.vat)}</td>
              </tr>
              {Number(receipt.totals?.deposit) !== 0 && (
                <tr>
                  <td>davon Pfand</td>
                  <td className='r'>{euro(receipt.totals?.deposit)}</td>
                </tr>
              )}
            </tbody>
          </table>

          <hr />
          {receipt.tse_failed ? (
            <div className='warn'>Sicherheitseinrichtung ausgefallen</div>
          ) : (
            receipt.tse && (
              <>
                <div className='tse'>
                  <div>TSE-Seriennr.: {receipt.tse.serial_number}</div>
                  <div>Transaktion: {receipt.tse.transaction_number}</div>
                  <div>Signaturzähler: {receipt.tse.signature_counter}</div>
                  <div>
                    Beginn:{' '}
                    {receipt.tse.time_start
                      ? moment
                          .unix(receipt.tse.time_start)
                          .format('DD.MM.YYYY HH:mm:ss')
                      : '—'}
                  </div>
                  <div>
                    Ende:{' '}
                    {receipt.tse.time_end
                      ? moment
                          .unix(receipt.tse.time_end)
                          .format('DD.MM.YYYY HH:mm:ss')
                      : '—'}
                  </div>
                </div>
                {receipt.tse_qr && (
                  <div className='qrblock'>
                    <img src={receipt.tse_qr} alt='TSE-Signatur' />
                    <div className='qrlabel'>TSE-Signatur</div>
                  </div>
                )}
              </>
            )
          )}

          {receipt.online_qr && (
            <div className='qrblock'>
              <img src={receipt.online_qr} alt='Online-Beleg' />
              <div className='qrlabel'>
                Online-Beleg — scannen, speichern &amp; per E-Mail senden
              </div>
            </div>
          )}

          <div className='foot'>{receipt.footer_text}</div>
        </div>
      )}
    </div>
  );
};

export default FiscalReceipt;
